import React, { useState, useEffect, useRef } from 'react';
import { useAI } from '../hooks/useAI';
import { useSpeech } from '../hooks/useSpeech';
import { useJarvisState } from '../hooks/useJarvisState';
import 'regenerator-runtime/runtime';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import MessageList from './chat/MessageList';
import ChatInput from './chat/ChatInput';
import VoiceSelector from './chat/VoiceSelector';
import SplineScene from './SplineScene';
import SoundEffects from './SoundEffects';
import StatusIndicator from './StatusIndicator';
import { Message } from '../types';
import { voiceService } from '../services/voice';

const JarvisInterface: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [voiceResponseEnabled, setVoiceResponseEnabled] = useState(true);
  const lastTranscriptRef = useRef('');
  const silenceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const { processMessage, isProcessing } = useAI();
  const { speak, stop, isSpeaking } = useSpeech();
  const { state: jarvisState, setState: setJarvisState } = useJarvisState();
  
  const {
    transcript,
    resetTranscript,
    browserSupportsSpeechRecognition,
    listening
  } = useSpeechRecognition();

  // Atualiza o estado do Jarvis quando está processando
  useEffect(() => {
    if (isProcessing) {
      setJarvisState('thinking');
    }
  }, [isProcessing]);

  // Atualiza o estado do Jarvis quando está falando
  useEffect(() => {
    if (isSpeaking) {
      setJarvisState('speaking');
    } else if (!isProcessing && !listening) {
      setJarvisState('idle');
    }
  }, [isSpeaking]);

  // Atualiza o texto quando o transcript muda
  useEffect(() => {
    if (transcript && transcript !== lastTranscriptRef.current) {
      setInputText(transcript);
      lastTranscriptRef.current = transcript;
      
      // Reseta o timeout anterior
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
      }
      
      // Configura um novo timeout para detectar silêncio
      silenceTimeoutRef.current = setTimeout(() => {
        if (listening && transcript.trim() && transcript === lastTranscriptRef.current) {
          handleSendMessage();
          handleStopListening();
        }
      }, 1200); // Reduzido para 1.2 segundos
    }
  }, [transcript]);

  // Limpa o timeout quando o componente é desmontado ou quando para de ouvir
  useEffect(() => {
    if (!listening && silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
    }
    return () => {
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
      }
    };
  }, [listening]);

  // Sincroniza o estado de listening
  useEffect(() => {
    if (listening) {
      setJarvisState('listening');
    } else if (!isProcessing && !isSpeaking) {
      setJarvisState('idle');
    }
  }, [listening]);

  const handleStartListening = () => {
    stop(); // Para a reprodução de voz atual
    resetTranscript();
    lastTranscriptRef.current = '';
    SpeechRecognition.startListening({ continuous: true });
  };

  const handleStopListening = () => {
    SpeechRecognition.stopListening();
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
    }
  };

  const handleVoiceChange = (provider: string, voiceId?: string) => {
    voiceService.setProvider(provider, voiceId);
  };

  const handleSendMessage = async () => {
    const messageText = inputText.trim();
    if (!messageText || isProcessing) return;

    const newMessage: Message = {
      id: Date.now().toString(),
      content: messageText,
      type: 'user',
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, newMessage]);
    setInputText('');
    resetTranscript();
    lastTranscriptRef.current = '';
    setJarvisState('thinking');

    try {
      const response = await processMessage(messageText);
      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: response,
        type: 'ai',
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, aiMessage]);

      if (voiceResponseEnabled) {
        setJarvisState('speaking');
        await speak(response);
        if (!listening) {
          setJarvisState('idle');
        }
      }
    } catch (error) {
      console.error('Error processing message:', error);
      setJarvisState('idle');
    }
  };

  return (
    <div className="flex flex-col h-full relative">
      {/* Background Effects */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Thinking Effect */}
        <div 
          className={`absolute inset-0 bg-gradient-radial from-primary/5 to-transparent transition-opacity duration-500 ${
            jarvisState === 'thinking' ? 'opacity-100' : 'opacity-0'
          }`} 
          style={{
            animation: jarvisState === 'thinking' ? 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' : 'none'
          }}
        />
        
        {/* Speaking Effect */}
        <div 
          className={`absolute inset-0 bg-gradient-radial from-accent-blue/5 to-transparent transition-opacity duration-500 ${
            jarvisState === 'speaking' ? 'opacity-100' : 'opacity-0'
          }`}
        />
        
        {/* Listening Effect */}
        <div 
          className={`absolute inset-0 bg-gradient-radial from-green-500/5 to-transparent transition-opacity duration-500 ${
            jarvisState === 'listening' ? 'opacity-100' : 'opacity-0'
          }`}
        />
        
        <SplineScene />
      </div>

      {/* Sound Effects */}
      <SoundEffects />

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 relative z-10">
        <div className="h-full flex flex-col justify-end">
          <div className="flex justify-end mb-4">
            <StatusIndicator />
          </div>
          <MessageList messages={messages} />
        </div>
      </div>

      {/* Input area */}
      <div className="p-4 bg-surface-dark/50 backdrop-blur-sm relative z-10">
        <div className="max-w-3xl mx-auto space-y-4">
          <VoiceSelector onVoiceChange={handleVoiceChange} />
          <ChatInput
            value={inputText}
            onChange={setInputText}
            onSend={handleSendMessage}
            isProcessing={isProcessing}
            voiceProps={{
              isListening: listening,
              onStartListening: handleStartListening,
              onStopListening: handleStopListening,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default JarvisInterface;